import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  PersonOff as PersonOffIcon,
  Groups as GroupsIcon,
  AttachMoney as PricingIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  Public as ZoneIcon,
  Route as RouteIcon,
  Functions as FormulaIcon,
  Tune as GlobalIcon,
  Category as CategoryIcon,
  Palette as PaletteIcon,
  AccessAlarm as RushHourIcon,
  AssignmentInd as ApplicationsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import ActiveWorkersSection from './ActiveWorkersSection';
import InactiveWorkersView from './InactiveWorkersView';
import ZonesManager from './pricing/ZonesManager';
import RoutePricesManager from './pricing/RoutePricesManager';
import FormulaSettingsManager from './pricing/FormulaSettingsManager';
import GlobalPricingSettings from './pricing/GlobalPricingSettings';
import RushHourFeeManager from './pricing/RushHourFeeManager';
import CompanySettingsPanel from './CompanySettingsPanel';
import CarTypeManager from './CarTypeManager';
import DriverApplicationsView from './DriverApplicationsView';
import EmailTemplateManager from './EmailTemplateManager';
import AgreementFormsManager from './AgreementFormsManager';

const SIDEBAR_WIDTH = 260;

// Sidebar navigation structure
const NAV_ITEMS = [
  {
    id: 'workers',
    label: 'Employees',
    icon: <GroupsIcon />,
    children: [
      { id: 'active', label: 'Active', icon: <PersonIcon /> },
      { id: 'pending', label: 'Pending', icon: <ApplicationsIcon /> },
      { id: 'inactive', label: 'Inactive', icon: <PersonOffIcon /> },
    ],
  },
  {
    id: 'pricing',
    label: 'Price Configuration',
    icon: <PricingIcon />,
    children: [
      { id: 'zones', label: 'Zones', icon: <ZoneIcon /> },
      { id: 'routes', label: 'Route Prices', icon: <RouteIcon /> },
      { id: 'formula', label: 'Formula Settings', icon: <FormulaIcon /> },
      { id: 'global', label: 'Global Settings', icon: <GlobalIcon /> },
      { id: 'rush-hour', label: 'Rush Hour Fees', icon: <RushHourIcon /> },
      { id: 'wait-time', label: 'Wait Time', icon: <TimerIcon /> },
      { id: 'add-ons', label: 'Add-Ons', icon: <CategoryIcon /> },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    children: [
      { id: 'car-types', label: 'Car Types', icon: <CategoryIcon /> },
      { id: 'scheduling', label: 'Scheduling & Operations', icon: <ScheduleIcon /> },
      { id: 'display', label: 'Display & Colors', icon: <PaletteIcon /> },
      { id: 'driver-application', label: 'Application Form', icon: <ApplicationsIcon /> },
      { id: 'email-templates', label: 'Email Templates', icon: <EmailIcon /> },
    ],
  },
];

const AdminManagement = ({ onNavigateToRideHistory, openMessagingModal, openMessagingDriverId, onMessagingModalClose, onMarkMessagesRead, onDirtyChange }) => {
  const { user } = useAuth();
  const { companyCode } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Parse URL to get active section + tab
  const getStateFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section') || 'workers';
    const tab = params.get('tab') || '';

    // Legacy support — remap old tab names to new ones
    if (!params.get('section') && ['drivers', 'dispatchers', 'inactive'].includes(tab)) {
      return { section: 'workers', tab: 'active' };
    }
    // Remap old tab IDs if still in URL
    const tabMap = { dispatchers: 'active', drivers: 'active', applications: 'pending' };
    const resolvedTab = tabMap[tab] || tab;

    return { section, tab: resolvedTab || getDefaultTab(section) };
  };

  const getDefaultTab = (section) => {
    const group = NAV_ITEMS.find(g => g.id === section);
    return group?.children?.[0]?.id || '';
  };

  const initialState = getStateFromUrl();
  const [activeSection, setActiveSection] = useState(initialState.section);
  const [activeTab, setActiveTab] = useState(initialState.tab);
  const [expandedSections, setExpandedSections] = useState({ [initialState.section]: true });
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  // Sync with URL on location change
  useEffect(() => {
    const state = getStateFromUrl();
    setActiveSection(state.section);
    setActiveTab(state.tab);
    setExpandedSections(prev => ({ ...prev, [state.section]: true }));
  }, [location.search]);

  const updateUrl = (section, tab) => {
    const params = new URLSearchParams();
    params.set('section', section);
    params.set('tab', tab);
    const basePath = companyCode ? `/${companyCode}/dispatch` : '/dispatch';
    navigate(`${basePath}/admin?${params.toString()}`, { replace: true });
  };

  const handleToggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleSelectTab = (sectionId, tabId) => {
    if (settingsDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    setActiveSection(sectionId);
    setActiveTab(tabId);
    updateUrl(sectionId, tabId);
    if (isMobile) setMobileOpen(false);
  };

  // Forward settingsDirty up to MainDashboard
  useEffect(() => {
    onDirtyChange?.(settingsDirty);
  }, [settingsDirty, onDirtyChange]);

  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      if (!user?.userId) {
        setIsAuthorized(false);
        return;
      }
      const roleData = await adminAPI.user.isAdmin(user.userId);
      setIsAuthorized(roleData.isAdmin);
    } catch (error) {
      console.error('Failed to check admin access:', error);
      setIsAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Checking permissions...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth="lx" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <LockIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={2}>
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Contact your administrator if you believe this is an error.
          </Typography>
        </Box>
      </Container>
    );
  }

  const renderContent = () => {
    // Workers
    if (activeSection === 'workers') {
      switch (activeTab) {
        case 'active':
          return (
            <ActiveWorkersSection
              onNavigateToRideHistory={onNavigateToRideHistory}
              openMessagingModal={openMessagingModal}
              openMessagingDriverId={openMessagingDriverId}
              onMessagingModalClose={onMessagingModalClose}
              onMarkMessagesRead={onMarkMessagesRead}
            />
          );
        case 'inactive':
          return <InactiveWorkersView />;
        case 'pending':
          return <DriverApplicationsView />;
        default:
          return (
            <ActiveWorkersSection
              onNavigateToRideHistory={onNavigateToRideHistory}
              openMessagingModal={openMessagingModal}
              openMessagingDriverId={openMessagingDriverId}
              onMessagingModalClose={onMessagingModalClose}
              onMarkMessagesRead={onMarkMessagesRead}
            />
          );
      }
    }

    // Pricing
    if (activeSection === 'pricing') {
      if (activeTab === 'wait-time' || activeTab === 'add-ons') {
        return <CompanySettingsPanel visibleSection={activeTab} onDirtyChange={(dirty) => { setSettingsDirty(dirty); }} />;
      }
      if (activeTab === 'rush-hour') {
        return <RushHourFeeManager />;
      }
      switch (activeTab) {
        case 'zones':
          return <ZonesManager />;
        case 'routes':
          return <RoutePricesManager />;
        case 'formula':
          return <FormulaSettingsManager />;
        case 'global':
          return <GlobalPricingSettings />;
        default:
          return <ZonesManager />;
      }
    }

    // Settings — pass the category to CompanySettingsPanel
    if (activeSection === 'settings') {
      if (activeTab === 'car-types') {
        return <CarTypeManager />;
      }
      if (activeTab === 'email-templates') {
        return <EmailTemplateManager />;
      }
      if (activeTab === 'driver-application') {
        return (
          <>
            <CompanySettingsPanel visibleSection="driver-application" onDirtyChange={(dirty) => { setSettingsDirty(dirty); }} />
            <Divider sx={{ my: 4 }} />
            <AgreementFormsManager />
          </>
        );
      }
      return <CompanySettingsPanel visibleSection={activeTab} onDirtyChange={(dirty) => { setSettingsDirty(dirty); }} />;
    }

    return null;
  };

  // Get the label for the current active tab
  const getActiveLabel = () => {
    const group = NAV_ITEMS.find(g => g.id === activeSection);
    const child = group?.children?.find(c => c.id === activeTab);
    return child?.label || group?.label || 'Admin';
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      {/* Sidebar header */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" fontWeight={700} noWrap>
          Admin
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {user?.username || 'Admin'}
        </Typography>
      </Box>
      <Divider />

      {/* Navigation list */}
      <List component="nav" sx={{ flex: 1, py: 1 }}>
        {NAV_ITEMS.map((group) => (
          <React.Fragment key={group.id}>
            {/* Section header (collapsible) */}
            <ListItemButton
              onClick={() => handleToggleSection(group.id)}
              sx={{
                py: 1.2,
                px: 2.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: expandedSections[group.id] ? 'primary.main' : 'text.secondary' }}>
                {group.icon}
              </ListItemIcon>
              <ListItemText
                primary={group.label}
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: expandedSections[group.id] ? 'primary.main' : 'text.primary',
                }}
              />
              {expandedSections[group.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </ListItemButton>

            {/* Sub-items */}
            <Collapse in={expandedSections[group.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.children.map((child) => {
                  const isActive = activeSection === group.id && activeTab === child.id;
                  return (
                    <ListItemButton
                      key={child.id}
                      onClick={() => handleSelectTab(group.id, child.id)}
                      selected={isActive}
                      sx={{
                        pl: 4.5,
                        py: 0.8,
                        borderLeft: isActive ? '3px solid' : '3px solid transparent',
                        borderLeftColor: isActive ? 'primary.main' : 'transparent',
                        bgcolor: isActive ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        '&.Mui-selected': {
                          bgcolor: 'action.selected',
                          '&:hover': { bgcolor: 'action.selected' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: isActive ? 'primary.main' : 'text.secondary' }}>
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{ position: 'fixed', top: { xs: 64, sm: 72 }, left: 8, zIndex: 1201, bgcolor: 'background.paper', boxShadow: 1 }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar — permanent on desktop, drawer on mobile */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              top: { xs: 56, sm: 64 },
              height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Paper
          elevation={0}
          square
          sx={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {sidebarContent}
        </Paper>
      )}

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Content header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {getActiveLabel()}
          </Typography>
        </Box>

        {/* Content */}
        {renderContent()}
      </Box>
    </Box>
  );
};

export default AdminManagement;